/* What an Event made — STORY-022.
 *
 * ── the figures are a table, and that is the accessibility requirement ──────
 * A reader scans DOWN a column here — income against income, profit against
 * profit — which is exactly what `data-table.css` says a table is for. Tiles
 * are for one headline number; a currency-by-figure grid is a table, and the
 * story asks for headers by name.
 *
 * ── nothing is summed across currencies ─────────────────────────────────────
 * One row per currency, and no total row. A total row would need a rate this
 * product does not have and would not know the date of.
 *
 * ── a loss is never colour alone ────────────────────────────────────────────
 * It is said in words beside the figure. `design.md` forbids colour carrying
 * meaning on its own, and a red number is invisible to a greyscale screen, a
 * screen reader, and a good proportion of readers.
 *
 * ── the percentage is computed HERE, from the two integers ──────────────────
 * Both sides travel as minor units and the division happens once, at display.
 * A percentage computed on the server and formatted here would be rounded
 * twice.
 */

import type { EventMoneyResponse } from '@eventuley/contracts';
import { formatMoney, money, parseCurrency } from '@eventuley/domain';
import { NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Callout } from '../../../shared/ui/index.js';
import { useEventMoney } from '../api/index.js';

export function EventMoney({
  organisationId,
  eventId,
}: {
  readonly organisationId: string;
  readonly eventId: string;
}) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const { data, error, isLoading } = useEventMoney(organisationId, eventId);

  /**
   * Minor units to something a person reads, in their own locale.
   *
   * The digit count comes from the CURRENCY, never a constant — JPY has none
   * and KWD has three. A code the runtime does not know shows the bare number
   * and the code rather than a guessed symbol, because a wrong symbol on a
   * real figure is worse than no symbol.
   */
  const format = (minor: number, currency: string) => {
    const code = parseCurrency(currency);
    return code ? formatMoney(money(minor, code), locale) : `${minor} ${currency}`;
  };

  if (error) {
    return (
      <section className="settings__section">
        <h2>{t('event_money.heading')}</h2>
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      </section>
    );
  }

  if (isLoading || !data) {
    return (
      <section className="settings__section">
        <h2>{t('event_money.heading')}</h2>
        <p className="field__hint">{t('common.loading')}</p>
      </section>
    );
  }

  return (
    <section className="settings__section">
      <h2>{t('event_money.heading')}</h2>

      {data.figures.length === 0 ? (
        /* Zero is a fact, not a blank — and this is most Events most of the
           time, so it must not read as something having gone wrong. */
        <p className="field__hint">{t('event_money.nothing_yet')}</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <caption className="sr-only">{t('event_money.table_caption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('event_money.currency_column')}</th>
                <th scope="col" className="num">
                  {t('money.direction.income')}
                </th>
                <th scope="col" className="num">
                  {t('money.direction.expense')}
                </th>
                <th scope="col" className="num">
                  {t('event_money.profit_column')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.figures.map((figure) => (
                <tr key={figure.currency}>
                  <th scope="row">{figure.currency}</th>
                  <td className="num">{format(figure.incomeMinor, figure.currency)}</td>
                  <td className="num">{format(figure.expenseMinor, figure.currency)}</td>
                  <td className="num">
                    {figure.profitMinor < 0
                      ? /* Said in words. A red number is invisible to a
                           greyscale screen and to a screen reader alike. */
                        t('event_money.loss', {
                          amount: format(Math.abs(figure.profitMinor), figure.currency),
                        })
                      : format(figure.profitMinor, figure.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BudgetLine data={data} format={format} />
      <RevenueLine data={data} format={format} />
    </section>
  );
}

function BudgetLine({
  data,
  format,
}: {
  readonly data: EventMoneyResponse;
  readonly format: (minor: number, currency: string) => string;
}) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();

  if (data.budget === null) {
    /* "Not set" and "0% used" are different facts, and the second is a lie
       about the first. No division happens here at all. */
    return <p className="field__hint">{t('event_money.budget_not_set')}</p>;
  }

  const { currency, budgetMinor, spentMinor } = data.budget;
  /* Computed here, from the two integers, once. A budget of zero would divide
     by nothing — it cannot be saved, and this is the guard that says so rather
     than rendering Infinity. */
  const percent =
    budgetMinor > 0
      ? new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
          spentMinor / budgetMinor,
        )
      : null;

  /* Every OTHER currency the Event has spent in. A percentage spanning two
     currencies would need a rate, so those are named as uncompared rather than
     folded in — which is what the story asks for by name. */
  const uncompared = data.figures
    .filter((figure) => figure.currency !== currency && figure.expenseMinor > 0)
    .map((figure) => figure.currency);

  return (
    <>
      <p className="field__hint">
        {percent === null
          ? t('event_money.budget_zero', { budget: format(budgetMinor, currency) })
          : t('event_money.budget_used', {
              percent,
              spent: format(spentMinor, currency),
              budget: format(budgetMinor, currency),
            })}
      </p>
      {uncompared.length > 0 ? (
        <p className="field__hint">
          {t('event_money.budget_not_set_for', { currencies: uncompared.join(', ') })}
        </p>
      ) : null}
    </>
  );
}

function RevenueLine({
  data,
  format,
}: {
  readonly data: EventMoneyResponse;
  readonly format: (minor: number, currency: string) => string;
}) {
  const t = useTranslate();

  if (data.revenue === null) return null;

  const { currency, expectedMinor, actualMinor } = data.revenue;
  /* The gap is usually the most useful number on the page, and the reference
     workbook stored both figures without ever putting them together. A
     subtraction of two integers, at display. */
  const varianceMinor = actualMinor - expectedMinor;

  return (
    <p className="field__hint">
      {varianceMinor < 0
        ? t('event_money.revenue_short', {
            expected: format(expectedMinor, currency),
            actual: format(actualMinor, currency),
            gap: format(Math.abs(varianceMinor), currency),
          })
        : t('event_money.revenue_over', {
            expected: format(expectedMinor, currency),
            actual: format(actualMinor, currency),
            gap: format(varianceMinor, currency),
          })}
    </p>
  );
}
